import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import App from './App';
import { PublicClientApplication } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import config, { backendConfig, scopeConfig, environmentConfig, appConfig } from "./authConfig";

const msalInstance = new PublicClientApplication(config.msal);

// Helper function to log to both console and localStorage
function debugLog(message, data = null) {
  if (!environmentConfig.enableDebugLogs && !message.includes("ERROR")) {
    return; // Skip debug logs in production unless it's an error
  }

  const timestamp = new Date().toISOString();
  const logEntry = `[${timestamp}] ${message}`;

  console.log(logEntry, data);

  // Store in localStorage for persistence (only in development)
  if (environmentConfig.isDevelopment) {
    const existingLogs = localStorage.getItem('msal_debug_logs') || '';
    const newLog = `${logEntry}${data ? ': ' + JSON.stringify(data, null, 2) : ''}\n`;
    localStorage.setItem('msal_debug_logs', existingLogs + newLog);
  }
}

async function initializeApp() {
  try {
    debugLog("=== STARTING MSAL INITIALIZATION ===");
    debugLog("Environment", environmentConfig.isDevelopment ? "Development" : "Production");
    debugLog("Current URL", window.location.href);
    debugLog("URL pathname", window.location.pathname);
    debugLog("URL search", window.location.search);
    debugLog("URL hash", window.location.hash);

    await msalInstance.initialize();
    debugLog("MSAL initialized successfully");

    if (window.location.pathname === '/auth/callback') {
      debugLog("=== ON CALLBACK PAGE ===");

      if (window.location.hash) {
        debugLog("Hash fragment present, waiting for MSAL to process...");
        await new Promise(resolve => setTimeout(resolve, 2000));

        const response = await msalInstance.handleRedirectPromise();
        debugLog('handleRedirectPromise response after hash wait', response);

        if (response) {
          debugLog('SUCCESS: Auth response found!');
          msalInstance.setActiveAccount(response.account);

          try {
            // Get access token with all required scopes
            const tokenResponse = await msalInstance.acquireTokenSilent({
              scopes: scopeConfig.all,
              account: response.account,
            });

            debugLog('Access token acquired', 'Token present: ' + !!tokenResponse.accessToken);

            // Send token to backend using configured endpoint
            const sessionResponse = await fetch(backendConfig.getUrl('pdiLogin'), {
              method: 'POST',
              headers: {
                'authorization': `Bearer ${tokenResponse.accessToken}`,
                'Content-Type': 'application/json'
              },
              credentials: 'include',
            });

            if (sessionResponse.ok) {
              debugLog('Backend session created successfully');
              window.history.replaceState({}, '', '/');
              debugLog('Navigated to home page');
            } else {
              const errorData = await sessionResponse.text();
              debugLog('ERROR: Backend session creation failed', errorData);

              // Parse error response for user-friendly message
              try {
                const errorJson = JSON.parse(errorData);
                let errorMessage = appConfig.errorMessages.authenticationFailed;

                switch (errorJson.error) {
                  case "UserNotInRequiredGroup":
                    errorMessage = appConfig.errorMessages.userNotInGroup;
                    break;
                  case "InvalidMicrosoftToken":
                    errorMessage = appConfig.errorMessages.invalidToken;
                    break;
                  case "GroupValidationFailed":
                    errorMessage = appConfig.errorMessages.groupValidationFailed;
                    break;
                  default:
                    errorMessage = errorJson.message || appConfig.errorMessages.backendError;
                }

                window.history.replaceState({}, '', `/Login?error=${encodeURIComponent(errorMessage)}`);
              } catch (parseError) {
                window.history.replaceState({}, '', `/Login?error=${encodeURIComponent(appConfig.errorMessages.backendError)}`);
              }
            }

          } catch (tokenError) {
            debugLog('ERROR: Token acquisition failed', tokenError.message);
            window.history.replaceState({}, '', `/Login?error=${encodeURIComponent(appConfig.errorMessages.tokenAcquisitionFailed)}`);
          }
        } else {
          debugLog('No response from handleRedirectPromise, checking existing accounts...');
          const accounts = msalInstance.getAllAccounts();
          if (accounts.length > 0) {
            debugLog('Found existing accounts, using first one');
            msalInstance.setActiveAccount(accounts[0]);
            window.history.replaceState({}, '', '/');
          } else {
            debugLog('No accounts found, auth failed');
            window.history.replaceState({}, '', `/Login?error=${encodeURIComponent(appConfig.errorMessages.authenticationFailed)}`);
          }
        }
      } else {
        debugLog("No hash fragment, trying normal redirect handling");
        const response = await msalInstance.handleRedirectPromise();
        debugLog('handleRedirectPromise response (no hash)', response);

        if (response) {
          msalInstance.setActiveAccount(response.account);
          window.history.replaceState({}, '', '/');
        } else {
          window.history.replaceState({}, '', `/Login?error=${encodeURIComponent(appConfig.errorMessages.authenticationFailed)}`);
        }
      }
    } else {
      debugLog("=== NOT ON CALLBACK PAGE ===");
      const response = await msalInstance.handleRedirectPromise();
      debugLog('handleRedirectPromise response (other page)', response);

      if (response) {
        msalInstance.setActiveAccount(response.account);
      }
    }

    const finalAccounts = msalInstance.getAllAccounts();
    debugLog('=== FINAL STATE BEFORE RENDER ===');
    debugLog('Final accounts length', finalAccounts.length);
    debugLog('Active account', msalInstance.getActiveAccount()?.username || 'None');

    // Render app
    const root = ReactDOM.createRoot(document.getElementById("root"));
    root.render(
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    );
  } catch (error) {
    debugLog("=== MSAL ERROR ===", error.message);

    // Still render the app
    const root = ReactDOM.createRoot(document.getElementById("root"));
    root.render(
      <MsalProvider instance={msalInstance}>
        <App />
      </MsalProvider>
    );
  }
}

// Start the app
initializeApp();